/* The day, programmed — the 02.08 KANON gallery's `Dzien - pelny ekran` body, and the same body
 * narrowed into `Tydzien z panelem dnia`'s 604px dock.
 *
 * Every number is one of those two files'. Where they differ the panel's sits under `.panel`,
 * because the gallery narrows a handful of paddings and gaps for the dock and leaves the rest
 * alone: the warm-up rail, the superset frame, the card list and the two block headings are
 * identical in both, and only the scroller's padding, the block gaps and two radii move.
 *
 * ── `--text-3` carries no words here ────────────────────────────────────────────────────────
 * The standing deviation the week canvas records: KANON's tertiary ink measures 2.80:1 on a card
 * and 4.02:1 in the dark theme, so it fails AA at every size this app uses. The gallery reaches for
 * it on a few meta lines; those read `--text-2` here and the hierarchy is carried by size and
 * weight instead. Precedents: `docs/design/README.md`, `SessionCard.module.css`.
 *
 * ── There is ONE density, and it is the gallery's ───────────────────────────────────────────
 * A "Komfort / Zwarta" switch tightened the vertical rhythm out of three stylesheets at once until
 * 3 August 2026, when the owner removed it. What is left is the comfortable rhythm — the gallery's
 * own numbers, unqualified — so no rule in this feature is written twice at two heights.
 */

/* `position: relative` is the RULE SHEET's containing block: the mock covers the whole box, the
 * day's head and foot with it, so the sheet is portalled here rather than into the stage. Nothing
 * else in this feature depended on resolving past this element — every absolute box below sits
 * inside `.stage`, `.listPane` or a card, each of which is positioned already. */
.form {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--bg);
}

/* The day itself — everything the rule sheet covers and takes out of play. It carries the column
 * the form used to lay out directly, because `.form` now holds two children rather than one: the
 * day, and the empty box the sheet is portalled into. */
.content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/* The sheet's own box: empty, unpositioned and of no size, so it lays nothing out and the pane
 * inside it resolves against `.form`. It exists so Base UI's focus guards bracket the dialog and
 * nothing else — see `DaySheetHost`. */
.sheetHost {
  flex: none;
}

/* The panel is drawn on the sunken surface, which is what gives its cards their seam against the
 * week beside it — `Tydzien z panelem dnia` docks it at exactly that. */
.panel {
  background: var(--bg-sunken);
}

/* The list, the library beside it, and the rule sheet over the lower part of both. The gallery's
 * own `position:relative;flex:1;min-height:0;display:flex` — and the box the sheet's `top: 34%` is
 * measured against. */
.stage {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* ── The scroller ────────────────────────────────────────────────────────────────────────── */

.listPane {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  min-height: 0;
  padding: var(--space-14px) var(--space-14px) var(--h-add-dock);
  overflow: auto;
}

/* ── Stepping to another day is NOT a CSS animation any more ─────────────────────────────────
 * It was one until 9 September 2026: `swapA` / `swapB` on this element, `opacity .2 -> 1` over a
 * 6px rise at the mock's 320ms, replayed by `shared/lib/useSwapReplay`. The owner called it
 * dramatic and asked for smooth and fast, and the reason it read as a lurch was that it animated
 * the BODY — five cards blinking as one block, from an opacity low enough to read as a wipe.
 *
 * The step now runs on the body's CHILDREN, through Motion, from `lib/useDayStepMotion`, which
 * carries the shape and the owner's own numbers. There is deliberately nothing left here: a
 * second declaration of the same movement is the near-duplicate that ends up settled by chunk
 * order. The WEEK canvas keeps its own `swapA` / `swapB` in `WeekPane.module.css` — that one ends
 * with the columns' flex settle and is not what the owner was describing. */

.body:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* `padding: 10px 14px; gap: 7px` — the mock's scroll body, with the floating dock's height added
 * below so the last card can always be scrolled clear of the fade. */
.panel .body {
  gap: var(--space-7px);
  padding: var(--space-10px) var(--space-14px);
}

/* The mock's own last child of the scroll body: 44px of nothing, so the floating dock's fade never
 * stands over the session note. It is the SPACER and not the dock's 74px, because the fade's top
 * half is all but transparent — reserving the whole dock left a visible gap under the last field. */
.bodyEnd {
  flex: none;
  height: 44px;
}

/* ── The warm-up run ─────────────────────────────────────────────────────────────────────── */

.warmup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-11px) var(--space-13px) var(--space-13px);
  background: var(--warm-bg);
  border-left: var(--border-rail) solid var(--warm-blue);
  border-radius: var(--space-0) var(--radius-md) var(--radius-md) var(--space-0);
}

.panel .warmup {
  gap: var(--space-2px);
  padding: var(--space-6px) var(--space-2) var(--space-6px) var(--space-10px);
  border-radius: var(--space-0) var(--r-control) var(--r-control) var(--space-0);
}

.warmupHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.warmupTitle {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--warm-blue);
}

.warmupCounts {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--warm-blue);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}

.warmupList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

/* Centred, as the mock centres the run's own pill under it. It held a full-width dashed zone until
 * 7 September 2026, which is why it did not need to say so. */
.warmupActions {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  padding-top: var(--space-3px);
}

.spacer {
  flex: 1;
}

/* ── The two block headings ──────────────────────────────────────────────────────────────── */

.blockHead {
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  padding: var(--space-0) var(--space-2px);
}

.blockTitle {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-3);
}

.blockCounts {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* ── The list ────────────────────────────────────────────────────────────────────────────── */

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.panel .list {
  gap: var(--space-7px);
}

/* ── A card leaving the list ──────────────────────────────────────────────────────────────────
 *
 * The canon's `cardOut`, verbatim: the card fades and settles back a hair over the first 45%, then
 * its box collapses to nothing and pulls the 7px gap under it closed. The `max-height` ceiling is
 * the canon's own 520px — a number chosen to clear the tallest card rather than to describe one.
 *
 * **The removal is not deferred to play it.** `removeExercise` reaches the store on the press and
 * this is drawn from the view the block last had, `inert` and `aria-hidden` for as long as it runs.
 * An undo inside the window brings the real block back and drops this one in the same commit — the
 * two are never on screen together. `model/departures` carries the whole argument. */
@keyframes card-out {
  0% {
    max-height: var(--h-card-out-max);
    opacity: 1;
    transform: none;
  }

  45% {
    opacity: 0;
    transform: scale(0.985);
  }

  100% {
    max-height: 0;
    margin-bottom: var(--space-minus-7px);
    opacity: 0;
    transform: scale(0.985);
  }
}

.blockLeaving {
  overflow: hidden;
  pointer-events: none;
  list-style: none;
  animation-name: card-out;
  animation-duration: var(--dur-card-out);
  animation-timing-function: var(--ease-exit);
  animation-fill-mode: both;
}

/* The run-off under the last block, so a drop released just below the list still lands at the end
 * rather than on nothing. Presentational and empty on purpose. */
.listEnd {
  position: relative;
  height: var(--space-6);
  list-style: none;
}

.listEnd[data-target]::after {
  position: absolute;
  top: var(--space-1);
  right: var(--space-0);
  left: var(--space-0);
  height: var(--border-focus);
  content: "";
  background: var(--accent);
  border-radius: var(--radius-xs);
}

/* ── The superset ring ───────────────────────────────────────────────────────────────────────
 *
 * `Builder planu - tydzien - programator` draws a superset as ONE ring around its member cards —
 * an 18px radius in the accent, a `SUPERSET` pill riding the top edge, and the cards inside
 * stitched together (their own border and shadow drop; see `ExerciseBlock.module.css`). The 10px
 * `margin-top` is what leaves the pill somewhere to sit.
 *
 * It used to have a twin here, `.bracketPrep`, drawing the MINI-GROUP as a second frame in the same
 * shape. That went on 8 September 2026: the mock puts a lift's own warm-ups inside the lift's card,
 * in a railed box above its head, and `ExerciseBlock.module.css` owns that drawing. Nothing about
 * the tree or the operation changed with it — a mini-group is still a warm-up bound to the one
 * exercise it prepares, and still one list position.
 */

.bracket {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* The mock paints the ring 6px OUTSIDE its member cards — `left/right: -6px`, `top/bottom: -6px` at
 * the group's open and close ends — at an 18px radius in `rgba(255,90,30,.55)`. Here that is one
 * real border on the frame: 6px of padding puts the cards where the mock puts them, and the
 * negative side margins let the ring stand proud of the list column exactly as far. The 10px top
 * margin is what leaves the SUPERSET pill somewhere to sit. */
.bracketSuperset {
  padding: var(--space-6px);
  margin: var(--space-10px) var(--space-minus-6px) var(--space-0);
  border: var(--border-hairline) solid var(--superset-ring);
  border-radius: var(--radius-lg);
}


/* The pill's row. It RIDES the ring's top edge, which is why the frame carries a 10px top margin. */
.bracketHead {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  min-width: 0;
}

.bracketSuperset .bracketHead {
  position: absolute;
  top: var(--space-minus-8-5px);
  left: var(--space-10px);
}


/* The canon's filled `SUPERSET` marker. The one accent on a block that is otherwise neutral, and it
 * marks a real structural state rather than a decoration. */
.bracketSuperset .bracketTag {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 17px;
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-pl-sub);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--on-accent);
  white-space: nowrap;
  background: var(--accent);
  border-radius: var(--radius-xs);
}


/* ── The two ways into the library ───────────────────────────────────────────────────────────
 *
 * `Builder planu - tydzien - programator` draws both as WHITE PILLS: `#fff` under `#15130f`, a
 * hairline and a neutral two-part elevation, with a `+` before the block's noun. They were dashed
 * zones carrying a sentence about the drag until 7 September 2026; `DropZoneView` records the
 * reversal and what it did not take away.
 *
 * The fill and the ink are `--chip-solid-fill` / `--chip-solid-ink`, which are theme-INDEPENDENT and
 * already exist for the plan library's own white pill on its hero plate. That is the point rather
 * than a coincidence: a white pill's label must be dark in both themes, so `var(--text)` would
 * invert on Flame and vanish. Second consumer, so the tokens stay where they are.
 *
 * The shadow is neutral AND theme-independent (`--shadow-pl-thumb`, the same value in all three
 * theme blocks) rather than the `--shadow-card-lift` the ink-filled version used: a fill that does
 * not change between themes must not sit under an elevation that does, or the pill separates in one
 * theme and floats in the other. The coloured-glow ban is absolute outside `shared/ui/emberButton`,
 * and a pill that opens a catalogue is not the screen's one real action.
 *
 * This was an INK pill (`--text` fill, `--bg-card` ink) until 7 September 2026 — a black button on
 * Flame and a white one on Flame Dark. The mock's is white on both, which is what a theme-independent
 * fill means.
 *
 * ── Three measured corrections, 8 September 2026 (owner: "cień jakoś inaczej, czcionka chudsza") ─
 * Driving both surfaces at 1600 x 1000 and reading the computed styles off each button:
 *
 * · **The hairline was TRANSPARENT.** The mock's `whiteBtn` writes `1px solid rgba(17,17,19,.10)`;
 *   this drew the same width in `transparent`, so the white fill met the panel with nothing between
 *   them and the two-part elevation under it read as a different shadow. `--chip-solid-edge` is that
 *   value, theme-independent beside the fill and the ink it belongs to.
 * · **The type was being ANTI-ALIASED.** `tokens/base.css` sets `-webkit-font-smoothing: antialiased`
 *   for the whole app, which is right on this app's dark grounds and is what thinned the one label in
 *   the panel standing on WHITE — the mock declares nothing and gets the browser's subpixel default.
 *   Restored for this pill alone; nothing else in the app puts dark ink on a theme-independent fill.
 * · **The tracking was -0.015em** (`--ls-plan`) where the mock writes -0.01em. `--ls-heading` is that
 *   step; it is the generic -0.01em of the scale rather than a heading-only value.
 *
 * Measured after: 700 12.5px, -0.125px tracking, `1px solid rgba(17,17,19,.1)`, radius 15,
 * `0 16px`, `0 1px 2px rgba(0,0,0,.18), 0 8px 20px -6px rgba(0,0,0,.55)` — the mock's, value for
 * value, with .22 in the second shadow on Flame exactly as `--shadow-white-pill` already carried. */
.pill {
  display: inline-flex;
  flex: none;
  gap: var(--space-7px);
  align-items: center;
  font-family: var(--font-body);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  color: var(--chip-solid-ink);
  white-space: nowrap;
  cursor: pointer;
  background: var(--chip-solid-fill);

  /* Longhands, not the `border` shorthand: the value allowlist takes no `transparent` there, and
   * `--fix` merges the three back into one — so this stays split on purpose. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--chip-solid-edge);
  box-shadow: var(--shadow-white-pill);

  /* The one place in the app where dark ink stands on a fill that does not change with the theme —
   * see the header. Everything else keeps the shell's `antialiased`. */
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
}

/* The main work's, floating in the fade at the foot of the list — the mock's `whiteBtn(36, 12.5)`:
 * 36px at `0 16px`, and its radius is `round(36 / 2.4)` = 15. */
.pillMain {
  height: 36px;
  padding: var(--space-0) var(--space-4);
  font-size: var(--fs-note);
  pointer-events: auto;
  border-radius: var(--r-button);
}

/* The warm-up run's, a step smaller because it stands INSIDE the run's tinted block rather than
 * over the panel — the mock's own 24px against 36px. */
.pillWarmup {
  gap: var(--space-6px);
  height: 28px;
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-caption);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-warm-pill);
}

.pill:hover {
  filter: var(--cta-press);
}

.pill:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* A library row dragged over it. The same accent the run-off's landing line wears. */
.pill[data-target] {
  border-color: var(--accent);
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.pillGlyph {
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
}

/* ── The floating way into the library ───────────────────────────────────────────────────────
 *
 * `Builder planu - tydzien - programator` ends the panel with one action standing in a 74px fade
 * rather than with a dashed zone closing the list. The fade is `pointer-events: none`, so a card
 * under it is still clickable; the action inside it takes them back.
 *
 * The scroller reserves the dock's height at its foot (`.body` padding below), so the last card of
 * a long day can always be scrolled clear of it — a control floating over content that cannot be
 * moved out from under it is the clip this app does not ship.
 */
.addDock {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: var(--h-add-dock);
  padding-bottom: var(--space-14px);
  pointer-events: none;
}

/* The fade, and it is a PSEUDO-ELEMENT rather than the dock's own background — which is the whole
 * of the fix made on 8 September 2026.
 *
 * `mask-image` masks the element AND every descendant it paints, so a mask declared on the dock was
 * applied to the white pill standing inside it: measured, the pill's top edge sat at 32% of the
 * 74px ramp and rendered at 52% alpha, its bottom edge at 81% and 88%. The panel showing through a
 * fill that is meant to be solid white read exactly as the owner described it — a shadow falling ON
 * the button rather than under it. The pill's own two shadows were never the cause; they are the
 * mock's, outer, and unchanged: `0 1px 2px rgba(0,0,0,.18)` and `0 8px 20px -6px rgba(0,0,0,.55)`.
 *
 * A masked pseudo-element paints only itself, so the ramp is identical and nothing above it is
 * touched. `z-index: -1` puts it behind the dock's content inside the stacking context the dock's
 * own `z-index: 2` already creates — behind the pill, still in front of the list.
 *
 * The fade is the panel's own ground, MASKED — not a gradient token. A gradient would need a
 * literal colour stop per theme in `colors.css`; masking a solid `--bg-sunken` gets the same ramp
 * from the token that is already correct in both, and `mask-image` carries no design decision the
 * allowlist needs to police. */
.addDock::before {
  position: absolute;
  z-index: -1;
  inset: 0;
  background: var(--bg);
  mask-image: linear-gradient(180deg, rgb(0 0 0 / 0%) 0%, rgb(0 0 0 / 74%) 46%, rgb(0 0 0 / 96%) 100%);
  content: "";
}

.panel .addDock::before {
  background: var(--bg-sunken);
}

/* ── The list's foot: the day's rest seed, and the session note ───────────────────────────────
 *
 * The mock draws no cards here. The rest is ONE row — a label, the value, and an ⓘ holding what
 * changing it restamps — and the note is a label over a dashed field. What went with the cards is
 * two `--bg-card` panels, two shadows and two paragraphs of standing prose, on every day of every
 * plan. */

.foot {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  padding: var(--space-2px) var(--space-2px) var(--space-0);
}

.footLabel {
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-column);
  text-transform: uppercase;
  color: var(--text-2);
}

/* The SKIP-LINK contract, and it is the whole of why this row is written twice.
 *
 * The programator draws no day-rest block, and nothing it does not draw may be visible — but the
 * seed behind it is a real value, so the control cannot simply go. Clipped to a pixel it takes no
 * room and paints nothing; the moment focus lands inside it, it paints itself over the foot at full
 * size, which is what stops a keyboard user tabbing onto something they cannot see. Same shape as
 * `shared/ui/EscapeLink`, and the same reason. */
.restRow {
  position: absolute;
  bottom: var(--space-0);
  left: var(--space-0);
  z-index: 3;
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.restRow:focus-within {
  width: auto;
  height: auto;
  padding: var(--space-2) var(--space-10px);
  overflow: visible;
  clip-path: none;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-card);
}

.restField {
  width: 82px;
  padding: var(--space-5px) var(--space-9px);
  font-family: var(--font-num);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
  font-variant-numeric: tabular-nums;
}

.restField[readonly] {
  color: var(--text-2);
}

.restField:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.noteBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
}

/* The mock's `sessionNoteSt`, verbatim: `min-height: 38px`, `8px 10px`, radius 10, 11.5px/500 at a
 * relaxed line height, dashed `--border-2` over nothing. Its ground is the panel's, not a fill —
 * a dashed edge on a transparent box is this app's affordance code, and the same one the session
 * card's rename and the "+ Trening" tile already wear. */
.sessionNote {
  width: 100%;
  min-height: 38px;
  padding: var(--space-2) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text);
  resize: vertical;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-sm);
}

.sessionNote[readonly] {
  color: var(--text-2);
  resize: none;
}

.sessionNote:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── The warm-up run's own ⋯ ─────────────────────────────────────────────────────────────── */

.warmupMenu {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.warmupMenu::after {
  position: absolute;
  inset: var(--hit-extend);
  content: "";
}

.warmupMenu:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Sentences the gallery does not draw ─────────────────────────────────────────────────── */

/* The dock pointed at a day the draft no longer has — a token in the URL that outlived its day, or a
 * Cmd+Z that took its phase away underneath it. It was one dashed box holding one sentence until 13
 * September 2026; it is `shared/ui/EmptyState` now, under a head carrying the two controls that can
 * leave the state, because in the modal there was no other way out. NOT the empty-day card below:
 * that one invites an exercise in, and there is no day here to put one in.
 *
 * The card inside centres itself and owns its own type, so this declares only how much of the host
 * it is given: all of it, under the head. */
.missing {
  display: flex;
  flex: 1;
  min-height: 0;
  padding: var(--space-4);
}

/* The card takes the whole box it is given, which is what centres it: `EmptyState` centres its own
 * contents but sizes to them, so at flex-start it would sit against the left edge of a modal more
 * than a thousand pixels wide. Its own header names `flex: 1` as the one thing a caller may say. */
.missingCard {
  flex: 1;
}

/* ── The empty day's card (owner, 9 September 2026) ───────────────────────────────────────────
 * It was one dashed box holding a sentence at `--text-2`. The card keeps that box's language — the
 * dashed `--border-2` this app uses wherever a surface invites something into itself, the same edge
 * the session note and the week's "+ Trening" tile wear — and gives it the drawing, the two lines
 * and the action the owner asked for. The mock has no such state, so this shape is his; see the
 * component.
 *
 * The height is RESERVED rather than grown into: the state is decided from the same store read as
 * the list beside it, so it can never arrive late, and stating a floor here means the card is the
 * same size whether its sentence wraps to two lines or three. */
.emptyDay {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  min-height: 208px;
  padding: var(--space-4);
  text-align: center;
  background: var(--bg-card);
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);
}

/* What a cycle-scoped edit reaches, said once above the list. Drawn only in the calendar's day
 * editor — the builder has no cycle and no such distinction to make. Neutral, not accented: it is a
 * standing fact about the surface, not a warning about anything the trainer has done. */
.cycleNote {
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  background: var(--bg-inset);
  border-radius: var(--radius-md);
}

/* The keyboard instruction, once for the whole dock. Reachable by assistive technology through the
 * grips' `aria-describedby`; never drawn, because the gallery draws no such line. */
.moveHint {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
